import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  XCircle,
} from "lucide-react";
import { useGlobalContext } from "../../GlobalContext";
import useChargesAndVehicles from "../../Sevices/useChargesAndVehicles";

export default function PackerSlotsTab() {
  const {
    getPackerSlotDetailsApi,
    generatePackerSlotApi,
    updatePackerSlotsApi,
    getPackerSlotDetailsLoading,
    generatePackerSlotLoading,
    updatePackerSlotsLoading,
  } = useChargesAndVehicles();

  const { AdminId } = useGlobalContext();

  const [packerSlotGroups, setPackerSlotGroups] = useState([]);
  const [selectedSlotDate, setSelectedSlotDate] = useState("");
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  const [slotActionStatus, setSlotActionStatus] = useState("Y");
  const [updatingSlotId, setUpdatingSlotId] = useState(null);

  const [generateSlotForm, setGenerateSlotForm] = useState({
    starttime: new Date().toISOString().slice(0, 16),
    days: "7",
  });

  const normalizePackerSlotGroups = (apiResponse) => {
    const data =
      apiResponse?.data || apiResponse?.response || apiResponse?.result || [];

    const groups = Array.isArray(data) ? data : [];

    return groups.map((group) => ({
      date: String(group?.date || ""),
      slots: Array.isArray(group?.slotDetails)
        ? group.slotDetails.map((slot) => ({
            slotId: String(slot?.pk_slot_id || ""),
            slotTime: String(slot?.slot_time || ""),
            status: String(slot?.status || ""),
            isActive: String(slot?.status || "").toUpperCase() === "Y",
            createdOn: slot?.created_on || "",
            raw: slot,
          }))
        : [],
    }));
  };

  const formatSlotDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  };

  const formatFullSlotDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const selectedSlotGroup = useMemo(() => {
    return packerSlotGroups.find((group) => group.date === selectedSlotDate);
  }, [packerSlotGroups, selectedSlotDate]);

  const visibleSlots = selectedSlotGroup?.slots || [];

  const selectedDateStats = useMemo(() => {
    const active = visibleSlots.filter((slot) => slot.isActive).length;
    const inactive = visibleSlots.length - active;

    return {
      total: visibleSlots.length,
      active,
      inactive,
    };
  }, [visibleSlots]);

  const loadPackerSlots = async () => {
    const result = await getPackerSlotDetailsApi({
      userId: String(AdminId),
    });

    const groups = normalizePackerSlotGroups(result);

    setPackerSlotGroups(groups);

    if (!selectedSlotDate && groups[0]?.date) {
      setSelectedSlotDate(groups[0].date);
    }

    return groups;
  };

  useEffect(() => {
    loadPackerSlots();
  }, []);

  const handleGenerateSlotInput = (key, value) => {
    setGenerateSlotForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleGeneratePackerSlots = async () => {
    if (!generateSlotForm.starttime || !generateSlotForm.days) return;

    const days = Number(generateSlotForm.days || 0);

    if (days < 1 || days > 7) {
      return;
    }

    const startDateTime = new Date(generateSlotForm.starttime).toISOString();

    await generatePackerSlotApi(
      {
        adminId: String(AdminId),
        starttime: startDateTime,
        days,
      },
      () => {},
      async () => {
        await loadPackerSlots();
        setSelectedSlotIds([]);
      },
    );
  };

  const toggleSlotSelection = (slotId) => {
    setSelectedSlotIds((prev) => {
      const id = String(slotId);

      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }

      return [...prev, id];
    });
  };

  const handleSelectAllVisibleSlots = () => {
    const allIds = visibleSlots.map((slot) => slot.slotId);

    const allSelected =
      allIds.length > 0 && allIds.every((id) => selectedSlotIds.includes(id));

    if (allSelected) {
      setSelectedSlotIds((prev) => prev.filter((id) => !allIds.includes(id)));
      return;
    }

    setSelectedSlotIds((prev) => [...new Set([...prev, ...allIds])]);
  };

  const handleUpdateSelectedSlots = async () => {
    if (!selectedSlotDate || selectedSlotIds.length === 0) return;

    await updatePackerSlotsApi(
      {
        adminId: String(AdminId),
        date: selectedSlotDate,
        slot: selectedSlotIds,
        status: slotActionStatus,
      },
      () => {},
      async () => {
        await loadPackerSlots();
        setSelectedSlotIds([]);
      },
    );
  };

  const handleToggleSlotStatus = async (slot) => {
    if (!selectedSlotDate || !slot?.slotId) return;

    const nextStatus = slot.isActive ? "N" : "Y";
    const previousStatus = slot.status;
    const previousIsActive = slot.isActive;

    setUpdatingSlotId(slot.slotId);

    // update UI instantly without full reload
    setPackerSlotGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (group.date !== selectedSlotDate) return group;

        return {
          ...group,
          slots: group.slots.map((item) => {
            if (String(item.slotId) !== String(slot.slotId)) return item;

            return {
              ...item,
              status: nextStatus,
              isActive: nextStatus === "Y",
            };
          }),
        };
      }),
    );

    setSelectedSlotIds((prev) =>
      prev.filter((id) => String(id) !== String(slot.slotId)),
    );

    try {
      await updatePackerSlotsApi(
        {
          adminId: String(AdminId),
          date: selectedSlotDate,
          slot: [String(slot.slotId)],
          status: nextStatus,
        },
        () => {},
        () => {},
      );
    } catch (error) {
      // rollback if API fails
      setPackerSlotGroups((prevGroups) =>
        prevGroups.map((group) => {
          if (group.date !== selectedSlotDate) return group;

          return {
            ...group,
            slots: group.slots.map((item) => {
              if (String(item.slotId) !== String(slot.slotId)) return item;

              return {
                ...item,
                status: previousStatus,
                isActive: previousIsActive,
              };
            }),
          };
        }),
      );
    } finally {
      setUpdatingSlotId(null);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 via-white to-white px-4 py-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-orange-700">
            <CalendarDays size={13} />
            Packer Slots
          </div>

          <h3 className="mt-2 text-base font-semibold text-gray-900">
            Slot Management
          </h3>

          <p className="mt-0.5 text-sm text-gray-500">
            Generate delivery slots and control which pickup slots are
            available.
          </p>
        </div>

        <button
          type="button"
          onClick={loadPackerSlots}
          disabled={getPackerSlotDetailsLoading}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          {getPackerSlotDetailsLoading ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <RefreshCw size={15} />
          )}
          Refresh Slots
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 p-4 xl:grid-cols-[360px_1fr]">
        {/* Generate Slot Card */}
        <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
              <Plus size={18} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                Generate Slots
              </h4>
              <p className="mt-0.5 text-xs text-gray-500">
                Create slots from selected start date and number of days.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Start Date & Time
              </label>

              <input
                type="datetime-local"
                value={generateSlotForm.starttime}
                onChange={(e) =>
                  handleGenerateSlotInput("starttime", e.target.value)
                }
                className="h-10 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Generate For Days
              </label>

              <select
                value={generateSlotForm.days}
                onChange={(e) =>
                  handleGenerateSlotInput("days", e.target.value)
                }
                className="h-10 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              >
                <option value="">Select days</option>
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <option key={day} value={String(day)}>
                    {day} {day === 1 ? "Day" : "Days"}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleGeneratePackerSlots}
              disabled={
                generatePackerSlotLoading ||
                !generateSlotForm.starttime ||
                !generateSlotForm.days
              }
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {generatePackerSlotLoading ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Plus size={15} />
              )}
              Generate Slots
            </button>
          </div>
        </div>

        {/* Slot List Card */}

        <div className="no-scrollbar h-[650px] overflow-y-auto rounded-2xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">
                Available Slot Calendar
              </h4>

              <p className="mt-0.5 text-xs text-gray-500">
                Select slots and update them as active or inactive.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={slotActionStatus}
                onChange={(e) => setSlotActionStatus(e.target.value)}
                className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              >
                <option value="Y">Mark Active</option>
                <option value="N">Mark Inactive</option>
              </select>

              <button
                type="button"
                onClick={handleUpdateSelectedSlots}
                disabled={
                  updatePackerSlotsLoading || selectedSlotIds.length === 0
                }
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-gray-900 px-3 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {updatePackerSlotsLoading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Save size={15} />
                )}
                Update{" "}
                {selectedSlotIds.length > 0
                  ? `(${selectedSlotIds.length})`
                  : ""}
              </button>
            </div>
          </div>

          {getPackerSlotDetailsLoading ? (
            <div className="space-y-3 p-4">
              <div className="flex gap-2 overflow-hidden">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="h-16 w-32 shrink-0 animate-pulse rounded-2xl bg-gray-100"
                  />
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-16 animate-pulse rounded-xl bg-gray-100"
                  />
                ))}
              </div>
            </div>
          ) : packerSlotGroups.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
                <CalendarDays size={22} />
              </div>

              <p className="mt-3 text-sm font-semibold text-gray-800">
                No slots generated yet
              </p>

              <p className="mt-1 text-sm text-gray-500">
                Generate slots first to manage active and inactive pickup
                windows.
              </p>
            </div>
          ) : (
            <div className="p-4">
              {/* Date Chips */}
              <div className="mb-4 flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {packerSlotGroups.map((group) => {
                  const active = selectedSlotDate === group.date;
                  const activeCount = group.slots.filter(
                    (slot) => slot.isActive,
                  ).length;

                  return (
                    <button
                      key={group.date}
                      type="button"
                      onClick={() => {
                        setSelectedSlotDate(group.date);
                        setSelectedSlotIds([]);
                      }}
                      className={`shrink-0 rounded-2xl border px-4 py-3 text-left transition-all ${
                        active
                          ? "border-orange-500 bg-orange-500 text-white shadow-md shadow-orange-100"
                          : "border-gray-200 bg-white text-gray-700 hover:border-orange-200 hover:bg-orange-50"
                      }`}
                    >
                      <p className="text-sm font-bold">
                        {formatSlotDate(group.date)}
                      </p>

                      <p
                        className={`mt-0.5 text-[11px] font-medium ${
                          active ? "text-white/85" : "text-gray-500"
                        }`}
                      >
                        {activeCount}/{group.slots.length} active
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Selected Date Header */}
              <div className="mb-3 flex flex-col gap-2 rounded-xl bg-gray-50 px-3 py-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatFullSlotDate(selectedSlotDate)}
                  </p>

                  <p className="mt-0.5 text-xs text-gray-500">
                    {selectedDateStats.total} total · {selectedDateStats.active}{" "}
                    active · {selectedDateStats.inactive} inactive
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleSelectAllVisibleSlots}
                  disabled={visibleSlots.length === 0}
                  className="inline-flex h-8 items-center justify-center rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Select / Clear All
                </button>
              </div>

              {/* Slot Grid */}
              {visibleSlots.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                  No slot found for this date.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 pr-1 sm:grid-cols-3 lg:grid-cols-4">
                  {visibleSlots.map((slot) => {
                    const selected = selectedSlotIds.includes(slot.slotId);
                    return (
                      <div
                        key={slot.slotId}
                        className={`rounded-xl border p-3 text-left transition-all ${
                          selected
                            ? "border-gray-900 bg-gray-900 text-white shadow-md"
                            : slot.isActive
                              ? "border-green-200 bg-green-50 text-gray-800 hover:border-green-300"
                              : "border-red-200 bg-red-50 text-gray-800 hover:border-red-300"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => toggleSlotSelection(slot.slotId)}
                            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                          >
                            <Clock
                              size={13}
                              className={
                                selected
                                  ? "text-white"
                                  : slot.isActive
                                    ? "text-green-600"
                                    : "text-red-600"
                              }
                            />

                            <span className="truncate text-xs font-bold">
                              {slot.slotTime}
                            </span>
                          </button>

                          {slot.isActive ? (
                            <CheckCircle2
                              size={14}
                              className={
                                selected ? "text-white" : "text-green-600"
                              }
                            />
                          ) : (
                            <XCircle
                              size={14}
                              className={
                                selected ? "text-white" : "text-red-600"
                              }
                            />
                          )}
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-2">
                          <p
                            className={`text-[11px] font-semibold ${
                              selected
                                ? "text-white/75"
                                : slot.isActive
                                  ? "text-green-700"
                                  : "text-red-700"
                            }`}
                          >
                            {slot.isActive ? "Active" : "Inactive"}
                          </p>

                          <button
                            type="button"
                            onClick={() => handleToggleSlotStatus(slot)}
                            disabled={updatingSlotId === slot.slotId}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 ${
                              slot.isActive ? "bg-green-500" : "bg-gray-300"
                            }`}
                            title={
                              slot.isActive ? "Mark inactive" : "Mark active"
                            }
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                slot.isActive
                                  ? "translate-x-5"
                                  : "translate-x-1"
                              }`}
                            />

                            {updatingSlotId === slot.slotId && (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <Loader2
                                  size={12}
                                  className="animate-spin text-white"
                                />
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
